import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useMemo, useSyncExternalStore } from 'react';
import { getRuntimeEnvValue } from './runtimeEnv';
const FLAG_REFRESH_INTERVAL_MS = 30000;
const FeatureFlagsContext = createContext({});
const FeatureFlagsActiveContext = createContext(null);
export function getConfiguredFeatureFlagEndpoint(endpointSource = 'telematics') {
    return endpointSource === 'telematics'
        ? getRuntimeEnvValue('TELEMATICS_FEATURE_FLAGS_ENDPOINT') ?? ''
        : getRuntimeEnvValue('UNIFIED_UI_FEATURE_FLAGS_ENDPOINT') ?? '';
}
function createFeatureFlagStore(endpointSource) {
    let initialized = false;
    let endpoint = '';
    let flags = {};
    let subscribers = [];
    let pollingActive = false;
    let pollingInterval = null;
    let pollingGeneration = 0;
    const notifySubscribers = () => {
        subscribers.forEach((callback) => callback());
    };
    const stopPolling = () => {
        if (pollingInterval !== null && typeof window !== 'undefined') {
            window.clearInterval(pollingInterval);
        }
        pollingInterval = null;
        pollingActive = false;
        pollingGeneration += 1;
    };
    const startPolling = () => {
        if (pollingActive || !endpoint) {
            return;
        }
        pollingActive = true;
        const fetchFlags = async () => {
            const generation = pollingGeneration;
            try {
                const response = await fetch(endpoint, {
                    method: 'GET',
                    credentials: 'include',
                    headers: { Accept: 'application/json' },
                });
                if (!response.ok)
                    return;
                const payload = (await response.json());
                const resolvedFlags = typeof payload === 'object' && payload !== null && 'data' in payload
                    ? payload.data
                    : payload;
                if (generation === pollingGeneration &&
                    resolvedFlags &&
                    typeof resolvedFlags === 'object') {
                    flags = resolvedFlags;
                    notifySubscribers();
                }
            }
            catch {
                // Polling should not block the app when endpoint is unavailable.
            }
        };
        void fetchFlags();
        if (typeof window !== 'undefined') {
            pollingInterval = window.setInterval(fetchFlags, FLAG_REFRESH_INTERVAL_MS);
        }
    };
    return {
        initialize() {
            if (initialized) {
                return;
            }
            endpoint = getConfiguredFeatureFlagEndpoint(endpointSource);
            initialized = true;
            if (subscribers.length > 0) {
                startPolling();
            }
        },
        subscribe(callback) {
            subscribers.push(callback);
            if (initialized) {
                startPolling();
            }
            return () => {
                subscribers = subscribers.filter((subscriber) => subscriber !== callback);
                if (subscribers.length === 0) {
                    stopPolling();
                }
            };
        },
        getSnapshot() {
            return flags;
        },
    };
}
const featureFlagStores = new Map();
function getFeatureFlagStore(endpointSource) {
    const existingStore = featureFlagStores.get(endpointSource);
    if (existingStore) {
        return existingStore;
    }
    const store = createFeatureFlagStore(endpointSource);
    featureFlagStores.set(endpointSource, store);
    return store;
}
export function initializeFeatureFlags(endpointSource = 'telematics') {
    getFeatureFlagStore(endpointSource).initialize();
}
export function FeatureFlagsRoot({ children, endpointSource = 'telematics', }) {
    const activeSource = useContext(FeatureFlagsActiveContext);
    if (activeSource === endpointSource) {
        return _jsx(_Fragment, { children: children });
    }
    return (_jsx(FeatureFlagsRootInner, { endpointSource: endpointSource, children: children ?? null }));
}
function FeatureFlagsRootInner({ children, endpointSource }) {
    const store = getFeatureFlagStore(endpointSource);
    store.initialize();
    const flags = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
    const value = useMemo(() => flags, [flags]);
    return (_jsx(FeatureFlagsActiveContext.Provider, { value: endpointSource, children: _jsx(FeatureFlagsContext.Provider, { value: value, children: children }) }));
}
export function useFeatureFlag(flagName) {
    const featureFlags = useContext(FeatureFlagsContext);
    return featureFlags[flagName] ?? false;
}
export function useStandaloneFeatureFlag(flagName, endpointSource = 'telematics', enabled = true) {
    const store = getFeatureFlagStore(endpointSource);
    if (enabled) {
        store.initialize();
    }
    const flags = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
    return flags[flagName] ?? false;
}
